import React from 'react';
import { Button, Tooltip } from '@mui/material';
import { Download } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface PdfDownloadButtonProps {
  contentRef: React.RefObject<HTMLElement>;
  fileName: string;
  documentTitle?: string;
  documentSubject?: string;
  buttonText?: string;
  loading?: boolean;
  setLoading?: (loading: boolean) => void;
  onError?: (error: Error) => void;
  className?: string;
  position?: 'left' | 'right' | 'center';
}

/**
 * A reusable PDF download button component that captures HTML content and converts it to a PDF
 */
export const PdfDownloadButton: React.FC<PdfDownloadButtonProps> = ({
  contentRef,
  fileName,
  documentTitle,
  documentSubject,
  buttonText = 'Download PDF',
  loading = false,
  setLoading,
  onError,
  className = '',
  position = 'right'
}) => {
  const downloadPdf = async () => {
    if (!contentRef.current) return;
    
    try {
      // Show loading state
      if (setLoading) setLoading(true);
      
      // Create a new jsPDF instance
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Set document properties
      pdf.setProperties({
        title: documentTitle || fileName,
        subject: documentSubject || fileName,
        author: 'iCanPitch',
        creator: 'iCanPitch Tool'
      });
      
      // Get the content element
      const content = contentRef.current;
      
      // Capture the content as canvas
      const canvas = await html2canvas(content, {
        scale: 2, // Higher scale for better quality
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      // Convert canvas to image
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      
      // Calculate the width and height for the PDF
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      // Add the image to the PDF
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      
      // If content is taller than a single page, add more pages
      const pageCount = Math.ceil(pdfHeight / pdf.internal.pageSize.getHeight());
      
      // If we need more than one page
      if (pageCount > 1) {
        for (let i = 1; i < pageCount; i++) {
          pdf.addPage();
          pdf.addImage(
            imgData,
            'JPEG',
            0,
            -(i * pdf.internal.pageSize.getHeight()),
            pdfWidth,
            pdfHeight
          );
        }
      }
      
      // Save the PDF with a sanitized filename
      const sanitizedFileName = fileName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      pdf.save(`${sanitizedFileName}.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
      if (onError && err instanceof Error) {
        onError(err);
      }
    } finally {
      if (setLoading) setLoading(false);
    }
  };

  // Determine position styling
  const positionStyle = {
    marginLeft: position === 'right' ? 'auto' : undefined,
    marginRight: position === 'left' ? 'auto' : undefined,
    margin: position === 'center' ? '0 auto' : undefined,
  };

  return (
    <Tooltip title="Download as PDF">
      <Button
        variant="outlined"
        color="primary"
        size="small"
        startIcon={<Download size={16} />}
        onClick={downloadPdf}
        disabled={loading}
        sx={{
          borderColor: 'primary.main',
          '&:hover': {
            borderColor: 'primary.dark',
            backgroundColor: 'rgba(59, 130, 246, 0.04)'
          },
          ...positionStyle
        }}
        className={className}
      >
        {loading ? 'Generating...' : buttonText}
      </Button>
    </Tooltip>
  );
};
